import React, { useContext, useEffect, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { BASE_URL_API } from '../../../References/urls'
import AuthContext from '../../../store/auth-context'
import styles from './AppVersionConfig.module.css'

const SEMVER_PATTERN = /^\d+\.\d+\.\d+$/

const compareSemver = (a, b) => {
    const [ma, mi, pa] = a.split('.').map(Number)
    const [mb, mib, pb] = b.split('.').map(Number)
    if (ma !== mb) return ma - mb
    if (mi !== mib) return mi - mib
    return pa - pb
}

const DEFAULT_FORM = {
    latestVersion: '',
    latestBuild: '',
    minRequiredVersion: '',
    minRequiredBuild: '',
    forceUpdate: false,
    softUpdateEnabled: false,
    updateTitle: '',
    updateMessage: '',
    softUpdateMessage: '',
    whatsNew: '',
    storeUrl: '',
    isMaintenance: false,
    maintenanceTitle: '',
    maintenanceMessage: '',
    maintenanceEta: '',
    graceBuilds: '5',
    debugBuildThreshold: '0',
    updateReason: ''
}

const getAuditDiff = (entry) => {
    const diffs = []
    if (entry.old_latest_version !== entry.new_latest_version || entry.old_latest_build !== entry.new_latest_build) {
        diffs.push(`Latest: ${entry.old_latest_version} (${entry.old_latest_build}) → ${entry.new_latest_version} (${entry.new_latest_build})`)
    }
    if (entry.old_min_required_version !== entry.new_min_required_version || entry.old_min_required_build !== entry.new_min_required_build) {
        diffs.push(`Min: ${entry.old_min_required_version} (${entry.old_min_required_build}) → ${entry.new_min_required_version} (${entry.new_min_required_build})`)
    }
    if (Number(entry.old_force_update) !== Number(entry.new_force_update)) {
        diffs.push(`Force Update: ${entry.old_force_update ? 'ON' : 'OFF'} → ${entry.new_force_update ? 'ON' : 'OFF'}`)
    }
    if (Number(entry.old_is_maintenance) !== Number(entry.new_is_maintenance)) {
        diffs.push(`Maintenance: ${entry.old_is_maintenance ? 'ON' : 'OFF'} → ${entry.new_is_maintenance ? 'ON' : 'OFF'}`)
    }
    return diffs
}

const formFromConfig = (c) => ({
    latestVersion: c.latest_version || '',
    latestBuild: c.latest_build?.toString() || '',
    minRequiredVersion: c.min_required_version || '',
    minRequiredBuild: c.min_required_build?.toString() || '',
    forceUpdate: c.force_update === '1' || c.force_update === 1,
    softUpdateEnabled: c.soft_update_enabled === '1' || c.soft_update_enabled === 1,
    updateTitle: c.update_title || '',
    updateMessage: c.update_message || '',
    softUpdateMessage: c.soft_update_message || '',
    whatsNew: c.whats_new || '',
    storeUrl: c.store_url || '',
    isMaintenance: c.is_maintenance === '1' || c.is_maintenance === 1,
    maintenanceTitle: c.maintenance_title || '',
    maintenanceMessage: c.maintenance_message || '',
    maintenanceEta: c.maintenance_eta ? c.maintenance_eta.replace(' ', 'T').slice(0, 16) : '',
    graceBuilds: c.grace_builds?.toString() || '5',
    debugBuildThreshold: c.debug_build_threshold?.toString() || '0',
    updateReason: ''
})

const AppVersionConfig = () => {
    const { token, userIdentifier } = useContext(AuthContext)
    const [activePlatform, setActivePlatform] = useState('android')
    const [configData, setConfigData] = useState(null)
    const [isNotFound, setIsNotFound] = useState(false)
    const [form, setForm] = useState(DEFAULT_FORM)
    const [isFetching, setIsFetching] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [maintenanceExpanded, setMaintenanceExpanded] = useState(false)
    const [showForceConfirm, setShowForceConfirm] = useState(false)
    const [warnings, setWarnings] = useState([])
    const [errors, setErrors] = useState({})

    const fetchConfig = async (platform) => {
        setIsFetching(true)
        setWarnings([])
        setErrors({})
        setIsNotFound(false)
        setConfigData(null)
        try {
            const res = await fetch(BASE_URL_API + "/getAppVersionConfig", {
                method: "POST",
                body: JSON.stringify({ token, userIdentifier, platform }),
            })
            const data = await res.json()
            if (data.success && data.response) {
                const { config, audit } = data.response
                setConfigData({ config, audit: audit || [] })
                setForm(formFromConfig(config))
                if (config.is_maintenance === '1' || config.is_maintenance === 1) setMaintenanceExpanded(true)
            } else if (data.errorType === 'NOT_FOUND') {
                setIsNotFound(true)
                setForm({ ...DEFAULT_FORM })
            } else {
                setForm({ ...DEFAULT_FORM })
            }
        } catch (_) {
            setForm({ ...DEFAULT_FORM })
        }
        setIsFetching(false)
    }

    useEffect(() => {
        fetchConfig(activePlatform)
    }, [activePlatform])

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors(prev => { const n = { ...prev }; delete n[field]; return n })
        }
    }

    const validate = () => {
        const errs = {}
        if (form.latestVersion && !SEMVER_PATTERN.test(form.latestVersion)) {
            errs.latestVersion = 'Must be in X.Y.Z format'
        }
        if (form.minRequiredVersion && !SEMVER_PATTERN.test(form.minRequiredVersion)) {
            errs.minRequiredVersion = 'Must be in X.Y.Z format'
        }
        if (form.latestBuild && (isNaN(form.latestBuild) || parseInt(form.latestBuild) <= 0)) {
            errs.latestBuild = 'Must be a positive integer'
        }
        if (form.minRequiredBuild && (isNaN(form.minRequiredBuild) || parseInt(form.minRequiredBuild) <= 0)) {
            errs.minRequiredBuild = 'Must be a positive integer'
        }
        if (
            form.latestVersion && form.minRequiredVersion &&
            SEMVER_PATTERN.test(form.latestVersion) && SEMVER_PATTERN.test(form.minRequiredVersion) &&
            compareSemver(form.minRequiredVersion, form.latestVersion) > 0
        ) {
            errs.minRequiredVersion = `Cannot exceed latestVersion (${form.latestVersion}) — would block all users`
        }
        if (form.latestBuild && form.minRequiredBuild && parseInt(form.minRequiredBuild) > parseInt(form.latestBuild)) {
            errs.minRequiredBuild = `Cannot exceed latestBuild (${form.latestBuild}) — would block all users`
        }
        if (form.maintenanceEta && new Date(form.maintenanceEta) <= new Date()) {
            errs.maintenanceEta = 'Must be a future datetime'
        }
        if (form.isMaintenance) {
            if (!form.maintenanceTitle.trim()) errs.maintenanceTitle = 'Required when maintenance mode is on'
            if (!form.maintenanceMessage.trim()) errs.maintenanceMessage = 'Required when maintenance mode is on'
        }
        if (!form.updateReason.trim() || form.updateReason.trim().length < 10) {
            errs.updateReason = 'Required — minimum 10 characters'
        }
        return errs
    }

    const handleSave = () => {
        const errs = validate()
        if (Object.keys(errs).length > 0) {
            setErrors(errs)
            toast.error('Please fix the errors before saving')
            return
        }
        if (form.forceUpdate) {
            setShowForceConfirm(true)
            return
        }
        submitForm()
    }

    const submitForm = async () => {
        setShowForceConfirm(false)
        setIsSaving(true)
        setWarnings([])

        const body = {
            token : token,
            adminId: userIdentifier,
            platform: activePlatform,
            forceUpdate: form.forceUpdate,
            softUpdateEnabled: form.softUpdateEnabled,
            isMaintenance: form.isMaintenance,
            updateReason: form.updateReason.trim()
        }

        if (form.latestVersion) body.latestVersion = form.latestVersion
        if (form.latestBuild) body.latestBuild = parseInt(form.latestBuild)
        if (form.minRequiredVersion) body.minRequiredVersion = form.minRequiredVersion
        if (form.minRequiredBuild) body.minRequiredBuild = parseInt(form.minRequiredBuild)
        if (form.updateTitle) body.updateTitle = form.updateTitle
        if (form.updateMessage) body.updateMessage = form.updateMessage
        if (form.softUpdateMessage) body.softUpdateMessage = form.softUpdateMessage
        if (form.whatsNew) body.whatsNew = form.whatsNew
        if (form.storeUrl) body.storeUrl = form.storeUrl
        if (form.maintenanceTitle) body.maintenanceTitle = form.maintenanceTitle
        if (form.maintenanceMessage) body.maintenanceMessage = form.maintenanceMessage
        if (form.maintenanceEta) body.maintenanceEta = form.maintenanceEta.replace('T', ' ') + ':00'
        if (form.graceBuilds !== '') body.graceBuilds = parseInt(form.graceBuilds)
        if (form.debugBuildThreshold !== '') body.debugBuildThreshold = parseInt(form.debugBuildThreshold)

        try {
            const res = await fetch(BASE_URL_API + "/updateAppVersion", {
                method: "POST",
                body: JSON.stringify(body)
            })
            const data = await res.json()
            if (data.success) {
                toast.success(data.message || 'Config saved successfully')
                if (data.warnings && data.warnings.length > 0) setWarnings(data.warnings)
                fetchConfig(activePlatform)
            } else {
                if (data.errorType === 'NO_CHANGE') {
                    toast.info('No changes detected — config is already up to date.')
                } else {
                    toast.error(data.message || 'Something went wrong')
                }
            }
        } catch (_) {
            toast.error('Network error — please try again')
        }

        setIsSaving(false)
    }

    const c = configData?.config

    return (
        <div className={styles.mainWrapper}>
            <ToastContainer />

            {showForceConfirm && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <div className={styles.modalTitle}>Force Update Confirmation</div>
                        <div className={styles.modalBody}>
                            This will block all users below version{' '}
                            <strong>{form.minRequiredVersion || '?'}</strong> (build{' '}
                            <strong>{form.minRequiredBuild || '?'}</strong>) on{' '}
                            <strong>{activePlatform}</strong>. Are you sure?
                        </div>
                        <div className={styles.modalActions}>
                            <button className={styles.confirmDangerBtn} onClick={submitForm}>
                                Yes, Enable Force Update
                            </button>
                            <button className={styles.cancelBtn} onClick={() => setShowForceConfirm(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.pageHeader}>
                <div className={styles.pageTitle}>App Version Config</div>
                <div className={styles.platformTabs}>
                    <button
                        className={activePlatform === 'android' ? styles.tabActive : styles.tab}
                        onClick={() => setActivePlatform('android')}
                    >
                        Android
                    </button>
                    <button
                        className={activePlatform === 'ios' ? styles.tabActive : styles.tab}
                        onClick={() => setActivePlatform('ios')}
                    >
                        iOS
                    </button>
                </div>
            </div>

            {isFetching ? (
                <div className={styles.loadingState}>Loading {activePlatform} config...</div>
            ) : (
                <div className={styles.pageBody}>

                    {isNotFound && (
                        <div className={styles.notFoundBanner}>
                            No config exists yet for <strong>{activePlatform === 'ios' ? 'iOS' : 'Android'}</strong>.
                            Fill in all required fields below to create it.
                        </div>
                    )}

                    {warnings.length > 0 && (
                        <div className={styles.warningsBanner}>
                            <div className={styles.warningsTitle}>Saved — but review these warnings</div>
                            {warnings.map((w, i) => (
                                <div key={i} className={styles.warningItem}>{w}</div>
                            ))}
                        </div>
                    )}

                    {/* ── Read view ── */}
                    {c && (
                        <div className={styles.readSection}>
                            <div className={styles.readSectionTitle}>Live Config</div>
                            <div className={styles.readGrid}>
                                <div className={styles.readCard}>
                                    <div className={styles.readCardLabel}>Latest Version</div>
                                    <div className={styles.readCardValue}>{c.latest_version}</div>
                                    <div className={styles.readCardSub}>Build {c.latest_build}</div>
                                </div>
                                <div className={styles.readCard}>
                                    <div className={styles.readCardLabel}>Min Required</div>
                                    <div className={styles.readCardValue}>{c.min_required_version}</div>
                                    <div className={styles.readCardSub}>Build {c.min_required_build}</div>
                                </div>
                                <div className={styles.readCard}>
                                    <div className={styles.readCardLabel}>Force Update</div>
                                    <div className={c.force_update === '1' || c.force_update === 1 ? styles.badgeDanger : styles.badgeOff}>
                                        {c.force_update === '1' || c.force_update === 1 ? 'ON' : 'OFF'}
                                    </div>
                                </div>
                                <div className={styles.readCard}>
                                    <div className={styles.readCardLabel}>Soft Update</div>
                                    <div className={c.soft_update_enabled === '1' || c.soft_update_enabled === 1 ? styles.badgeOn : styles.badgeOff}>
                                        {c.soft_update_enabled === '1' || c.soft_update_enabled === 1 ? 'ON' : 'OFF'}
                                    </div>
                                </div>
                                <div className={styles.readCard}>
                                    <div className={styles.readCardLabel}>Maintenance</div>
                                    <div className={c.is_maintenance === '1' || c.is_maintenance === 1 ? styles.badgeDanger : styles.badgeOff}>
                                        {c.is_maintenance === '1' || c.is_maintenance === 1 ? 'ACTIVE' : 'OFF'}
                                    </div>
                                </div>
                                <div className={styles.readCard}>
                                    <div className={styles.readCardLabel}>Grace Builds</div>
                                    <div className={styles.readCardValue}>{c.grace_builds}</div>
                                </div>
                            </div>
                            <div className={styles.readMeta}>
                                Last updated by <strong>{c.updated_by}</strong> at {c.updated_at}
                                {c.update_reason && <> — <em>"{c.update_reason}"</em></>}
                            </div>
                        </div>
                    )}

                    {/* ── Edit form ── */}
                    <div className={styles.formBody}>

                        {/* Release */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>Release</div>
                            <div className={styles.fieldGrid}>
                                <div className={styles.field}>
                                    <label className={styles.label}>
                                        Latest Version <span className={styles.req}>*</span>
                                    </label>
                                    <input
                                        value={form.latestVersion}
                                        onChange={e => handleChange('latestVersion', e.target.value)}
                                        placeholder="e.g. 2.0.4"
                                        className={errors.latestVersion ? styles.inputError : styles.input}
                                    />
                                    {errors.latestVersion && <div className={styles.errorMsg}>{errors.latestVersion}</div>}
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label}>
                                        Latest Build <span className={styles.req}>*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={form.latestBuild}
                                        onChange={e => handleChange('latestBuild', e.target.value)}
                                        placeholder="e.g. 48"
                                        className={errors.latestBuild ? styles.inputError : styles.input}
                                    />
                                    {errors.latestBuild && <div className={styles.errorMsg}>{errors.latestBuild}</div>}
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label}>
                                        Store URL <span className={styles.req}>*</span>
                                    </label>
                                    <input
                                        value={form.storeUrl}
                                        onChange={e => handleChange('storeUrl', e.target.value)}
                                        placeholder={
                                            activePlatform === 'android'
                                                ? 'market://details?id=in.stylelink.app'
                                                : 'itms-apps://itunes.apple.com/app/idXXXXXX'
                                        }
                                        className={errors.storeUrl ? styles.inputError : styles.input}
                                    />
                                    {errors.storeUrl && <div className={styles.errorMsg}>{errors.storeUrl}</div>}
                                </div>
                            </div>
                        </div>

                        {/* Update Policy */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>Update Policy</div>
                            <div className={styles.fieldGrid}>
                                <div className={styles.field}>
                                    <label className={styles.label}>
                                        Min Required Version <span className={styles.req}>*</span>
                                    </label>
                                    <input
                                        value={form.minRequiredVersion}
                                        onChange={e => handleChange('minRequiredVersion', e.target.value)}
                                        placeholder="e.g. 2.0.0"
                                        className={errors.minRequiredVersion ? styles.inputError : styles.input}
                                    />
                                    {errors.minRequiredVersion && <div className={styles.errorMsg}>{errors.minRequiredVersion}</div>}
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label}>
                                        Min Required Build <span className={styles.req}>*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={form.minRequiredBuild}
                                        onChange={e => handleChange('minRequiredBuild', e.target.value)}
                                        placeholder="e.g. 30"
                                        className={errors.minRequiredBuild ? styles.inputError : styles.input}
                                    />
                                    {errors.minRequiredBuild && <div className={styles.errorMsg}>{errors.minRequiredBuild}</div>}
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label}>Grace Builds</label>
                                    <input
                                        type="number"
                                        value={form.graceBuilds}
                                        onChange={e => handleChange('graceBuilds', e.target.value)}
                                        className={styles.input}
                                    />
                                    <div className={styles.hint}>Builds above latest allowed silently (store approval gap). Default: 5</div>
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label}>Debug Build Threshold</label>
                                    <input
                                        type="number"
                                        value={form.debugBuildThreshold}
                                        onChange={e => handleChange('debugBuildThreshold', e.target.value)}
                                        className={styles.input}
                                    />
                                    <div className={styles.hint}>Builds at or below this are never blocked (emulators/debug APKs). Default: 0</div>
                                </div>
                            </div>
                            <div className={styles.toggleRow}>
                                <div className={styles.toggleField}>
                                    <label className={styles.label}>Force Update</label>
                                    <div className={styles.hint}>
                                        Blocks all users below min version. Set OFF to let everyone through immediately.
                                    </div>
                                    <button
                                        className={form.forceUpdate ? styles.toggleDanger : styles.toggleOff}
                                        onClick={() => handleChange('forceUpdate', !form.forceUpdate)}
                                    >
                                        {form.forceUpdate ? 'ON — Users below min are blocked' : 'OFF — No one is blocked'}
                                    </button>
                                </div>
                                <div className={styles.toggleField}>
                                    <label className={styles.label}>Soft Update Enabled</label>
                                    <div className={styles.hint}>
                                        Dismissible nudge banner for users behind latest but above min.
                                    </div>
                                    <button
                                        className={form.softUpdateEnabled ? styles.toggleOn : styles.toggleOff}
                                        onClick={() => handleChange('softUpdateEnabled', !form.softUpdateEnabled)}
                                    >
                                        {form.softUpdateEnabled ? 'ON — Nudge banner shown' : 'OFF — No banner'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Screen Copy */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>Screen Copy</div>
                            <div className={styles.fieldGrid}>
                                <div className={styles.field}>
                                    <label className={styles.label}>Update Screen Title</label>
                                    <input
                                        value={form.updateTitle}
                                        onChange={e => handleChange('updateTitle', e.target.value)}
                                        placeholder="e.g. A better StyleLink is here"
                                        className={styles.input}
                                    />
                                </div>
                                <div className={styles.field}>
                                    <label className={styles.label}>Soft Banner Message</label>
                                    <input
                                        value={form.softUpdateMessage}
                                        onChange={e => handleChange('softUpdateMessage', e.target.value)}
                                        placeholder="e.g. A new version of StyleLink is available."
                                        className={styles.input}
                                    />
                                </div>
                                <div className={`${styles.field} ${styles.fullWidth}`}>
                                    <label className={styles.label}>Update Screen Message</label>
                                    <textarea
                                        value={form.updateMessage}
                                        onChange={e => handleChange('updateMessage', e.target.value)}
                                        placeholder="Body text shown on the force update screen"
                                        className={styles.textarea}
                                        rows={3}
                                    />
                                </div>
                                <div className={`${styles.field} ${styles.fullWidth}`}>
                                    <label className={styles.label}>What's New</label>
                                    <textarea
                                        value={form.whatsNew}
                                        onChange={e => handleChange('whatsNew', e.target.value)}
                                        placeholder={'• Faster job search\n• Profile improvements\n• Bug fixes'}
                                        className={styles.textarea}
                                        rows={4}
                                    />
                                    <div className={styles.hint}>One bullet per line, e.g. "• Faster job search"</div>
                                </div>
                            </div>
                        </div>

                        {/* Maintenance */}
                        <div className={styles.section}>
                            <div
                                className={styles.sectionHeader}
                                onClick={() => setMaintenanceExpanded(p => !p)}
                            >
                                <div className={styles.sectionTitle}>
                                    Maintenance Mode
                                    {form.isMaintenance && (
                                        <span className={styles.activeBadge}>ACTIVE</span>
                                    )}
                                </div>
                                <div className={styles.expandIcon}>
                                    {maintenanceExpanded ? '▲' : '▼'}
                                </div>
                            </div>

                            {maintenanceExpanded && (
                                <div className={styles.maintenanceBody}>
                                    <div className={styles.toggleField}>
                                        <label className={styles.label}>Maintenance Mode</label>
                                        <div className={styles.hint}>
                                            Overrides all version logic — entire app shows the maintenance screen.
                                        </div>
                                        <button
                                            className={form.isMaintenance ? styles.toggleDanger : styles.toggleOff}
                                            onClick={() => handleChange('isMaintenance', !form.isMaintenance)}
                                        >
                                            {form.isMaintenance ? 'ON — App is in maintenance' : 'OFF — App is live'}
                                        </button>
                                    </div>

                                    {form.isMaintenance && (
                                        <div className={styles.fieldGrid}>
                                            <div className={styles.field}>
                                                <label className={styles.label}>
                                                    Maintenance Title <span className={styles.req}>*</span>
                                                </label>
                                                <input
                                                    value={form.maintenanceTitle}
                                                    onChange={e => handleChange('maintenanceTitle', e.target.value)}
                                                    placeholder="e.g. We'll be right back"
                                                    className={errors.maintenanceTitle ? styles.inputError : styles.input}
                                                />
                                                {errors.maintenanceTitle && <div className={styles.errorMsg}>{errors.maintenanceTitle}</div>}
                                            </div>
                                            <div className={styles.field}>
                                                <label className={styles.label}>Estimated Back Time</label>
                                                <input
                                                    type="datetime-local"
                                                    value={form.maintenanceEta}
                                                    onChange={e => handleChange('maintenanceEta', e.target.value)}
                                                    className={errors.maintenanceEta ? styles.inputError : styles.input}
                                                />
                                                {errors.maintenanceEta && <div className={styles.errorMsg}>{errors.maintenanceEta}</div>}
                                            </div>
                                            <div className={`${styles.field} ${styles.fullWidth}`}>
                                                <label className={styles.label}>
                                                    Maintenance Message <span className={styles.req}>*</span>
                                                </label>
                                                <textarea
                                                    value={form.maintenanceMessage}
                                                    onChange={e => handleChange('maintenanceMessage', e.target.value)}
                                                    placeholder="StyleLink is undergoing scheduled maintenance. We'll be back shortly."
                                                    className={errors.maintenanceMessage ? styles.textareaError : styles.textarea}
                                                    rows={3}
                                                />
                                                {errors.maintenanceMessage && <div className={styles.errorMsg}>{errors.maintenanceMessage}</div>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Audit reason */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>Audit</div>
                            <div className={styles.fieldGrid}>
                                <div className={`${styles.field} ${styles.fullWidth}`}>
                                    <label className={styles.label}>
                                        Reason for Change <span className={styles.req}>*</span>
                                    </label>
                                    <textarea
                                        value={form.updateReason}
                                        onChange={e => handleChange('updateReason', e.target.value)}
                                        placeholder="e.g. Post 2.0.4 release — raising min to 2.0.0 to drop legacy API support"
                                        className={errors.updateReason ? styles.textareaError : styles.textarea}
                                        rows={3}
                                    />
                                    {errors.updateReason && <div className={styles.errorMsg}>{errors.updateReason}</div>}
                                    <div className={styles.hint}>
                                        Permanently logged with your admin ID. Write something meaningful.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.saveRow}>
                            <button
                                className={isSaving ? styles.saveBtnDisabled : styles.saveBtn}
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving
                                    ? 'Saving...'
                                    : `Save ${activePlatform === 'android' ? 'Android' : 'iOS'} Config`}
                            </button>
                        </div>
                    </div>

                    {/* ── Change history ── */}
                    {configData?.audit?.length > 0 && (
                        <div className={styles.auditSection}>
                            <div className={styles.auditSectionTitle}>
                                Change History
                                <span className={styles.auditCount}>{configData.audit.length}</span>
                            </div>
                            <div className={styles.auditList}>
                                {configData.audit.map((entry, i) => {
                                    const diffs = getAuditDiff(entry)
                                    return (
                                        <div key={i} className={styles.auditEntry}>
                                            <div className={styles.auditEntryHeader}>
                                                <span className={styles.auditBy}>{entry.changed_by}</span>
                                                <span className={styles.auditAt}>{entry.changed_at}</span>
                                            </div>
                                            <div className={styles.auditReason}>{entry.change_reason}</div>
                                            {diffs.length > 0 && (
                                                <div className={styles.auditDiff}>
                                                    {diffs.map((d, j) => (
                                                        <span key={j} className={styles.diffChip}>{d}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                </div>
            )}
        </div>
    )
}

export default AppVersionConfig
